// src/context/AuthContext.js (Updated)

import React, { createContext, useState, useContext, useEffect } from 'react';
// --- CHANGE 1: Import apiClient ---
import apiClient from '../api/apiClient'; // Adjust path if needed

const AuthContext = createContext();

// --- CHANGE 2: Remove API_BASE_URL definition here ---
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

// Define the key used to store the token in localStorage
// Ensure this matches apiClient.js
const AUTH_TOKEN_KEY = 'token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Start loading
  const [error, setError] = useState(null);

  // Effect for initial load - check token
  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    console.log("AuthContext: Initial load, token found:", !!token);
    if (token) {
       try {
           const decoded = JSON.parse(atob(token.split('.')[1]));
           console.log("AuthContext: Decoded token payload on initial load:", decoded);
           // Still okay to populate initial user state minimally from token here
           setUser({
             token: token,
             id: decoded.id,
             name: decoded.fullName,
             email: decoded.email
           });
           // For robustness, you might add a call here to apiClient.get('/api/profile')
           // to *verify* the token with the backend and get full user info.
       } catch (e) {
           console.error("AuthContext: Failed to decode token on initial load, logging out.", e);
           performLogout(); // Call internal logout
       }
    }
    setLoading(false);
  }, []);


  const login = async (identifier, password) => {
    setError(null);
    setLoading(true);
    try {
      // --- CHANGE 3: Use apiClient ---
      // apiClient already has the base URL and will add the token
      const response = await apiClient.post('/login', { identifier, password });
      // --- End Change 3 ---

      const data = response.data; // Axios response data is in response.data

      // Axios throws errors for non-2xx status codes, so we don't need response.ok check here.
      // If we reach here, the login was successful (status 2xx).
      console.log("AuthContext: Login successful, data:", data);
      const token = data.token;
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      setUser({
            token: token,
            id: data.user_id,
            name: data.full_name,
            email: data.email,
            phone_number: data.phone_number,
      });
      setError(null);
      setLoading(false);
      return true;

    } catch (error) {
       // Axios errors have response data in error.response.data
       const errorData = error.response?.data;
       const status = error.response?.status;
       console.error('AuthContext: Login API error:', error.response || error.message);

       // Use error message from backend response if available
       setError(errorData?.error || errorData?.detail || `Login failed (${status || 'Network Error'})`);
       setLoading(false);
       // Optional: Check if the error was already handled by the interceptor (e.g., for 401/403 redirect)
       if (error.handledByInterceptor) {
           console.log("AuthContext: Login error handled by interceptor.");
           // We might not need to return false here if redirect happened,
           // but returning false is safe.
           return false;
       }
       return false;
    }
  };

  const signup = async (full_name, email, phone_number, password) => {
     setError(null);
     setLoading(true);
     try {
       // --- CHANGE 4: Use apiClient ---
       // apiClient handles base URL, no need for token here
       const response = await apiClient.post('/signup', { full_name, email, phone_number, password });
       // --- End Change 4 ---

       const data = response.data; // Axios response data

       // Axios throws for non-2xx, so reaching here means success
       console.log("AuthContext: Signup successful.");
       setError(null);
       setLoading(false);
       return true;

     } catch (error) {
       const errorData = error.response?.data;
       const status = error.response?.status;
       console.error('AuthContext: Signup API error:', error.response || error.message);
       // Use error message from backend response if available
       setError(errorData?.error || errorData?.detail || `Signup failed (${status || 'Network Error'})`);
       setLoading(false);
       return false;
     }
  };

  // Define logout within the component scope
  const performLogout = () => {
      console.log("AuthContext: Logging out.");
      setUser(null);
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem('activeTab'); // Keep cleanup consistent
      localStorage.removeItem('chatState');
      setError(null);
      // Optionally force a reload or redirect to login after logout if needed
      // window.location.replace('/login');
  };

  // Expose performLogout as logout
  const logout = performLogout;


  const value = { user, login, signup, logout, loading, error };

  // Render children only after initial loading check is complete
  return (
      <AuthContext.Provider value={value}>
          {loading ? (
               <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '1.2em' }}>
                   Initializing Session...
               </div>
           ) : (
              children
          )}
      </AuthContext.Provider>
  );
}

// Custom hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}